import React from 'react';
import {
  Eye,
  EyeOff,
  Download,
  Info,
  Layers,
  Type,
  Sliders,
  Sparkles,
  Scissors,
  Hash,
  QrCode,
  Table as TableIcon,
  Image as ImageIcon,
} from 'lucide-react';
import { Barcode as BarcodeIcon } from 'lucide-react';
import {
  CanvasElement,
  PageConfig,
} from '../types';
import { getCurrencyDisplay, isAsciiSafeSymbol } from '../utils/currency';
import { generateCode39Bars, generateQRMatrix } from '../utils/generators';

// ─── Props ────────────────────────────────────────────────────────────────────

interface PrintPreviewModalProps {
  // visibility
  isPrintPreviewOpen: boolean;
  setIsPrintPreviewOpen: (open: boolean) => void;

  // canvas data
  elements: CanvasElement[];
  pageConfig: PageConfig;
  computedHeight: number;
  pageBehavior: 'fixed' | 'auto_expand' | 'multi_page';
  bilingualMode: boolean;
  translateTargetLang: string;

  // currency
  activeCurrency: string;
  currencyDisplayMode: 'symbol' | 'code';

  // layer visibility
  hiddenElementIds: string[];
  setHiddenElementIds: React.Dispatch<React.SetStateAction<string[]>>;

  // selection inside modal
  printSelectedElementId: string | null;
  setPrintSelectedElementId: (id: string | null) => void;

  // qr cache
  qrDataUrls: Record<string, string>;

  // helpers passed from App (keep stable refs with useCallback)
  handleExportPDF: () => void;
  handleZIndex: (id: string, action: 'front' | 'back' | 'up' | 'down') => void;
  computeTableTotals: (el: CanvasElement) => {
  subtotal: number;
  tax: number;
  secondTax?: number;
  discount: number;
  shipping: number;
  total: number;
};
  formatSerialNumber: (settings: any) => string;
  isSerialNoColumn: (col: any) => boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PrintPreviewModal: React.FC<PrintPreviewModalProps> = ({
  isPrintPreviewOpen,
  setIsPrintPreviewOpen,
  elements,
  pageConfig,
  computedHeight,
  pageBehavior,
  bilingualMode,
  translateTargetLang,
  activeCurrency,
  currencyDisplayMode,
  hiddenElementIds,
  setHiddenElementIds,
  printSelectedElementId,
  setPrintSelectedElementId,
  qrDataUrls,
  handleExportPDF,
  handleZIndex,
  computeTableTotals,
  formatSerialNumber,
  isSerialNoColumn,
}) => {
  if (!isPrintPreviewOpen) return null;

  const currencySymbol = getCurrencyDisplay(activeCurrency, currencyDisplayMode);

  const totalLabels: Record<string, { subtotal: string; total: string; tax: string; discount: string; shipping: string }> = {
    ar: { subtotal: 'المجموع الفرعي', total: 'الإجمالي الكلي', tax: 'ضريبة', discount: 'خصم', shipping: 'الشحن' },
    ko: { subtotal: '소계', total: '합계', tax: '세금', discount: '할인', shipping: '배송' },
    hi: { subtotal: 'उप-कुल', total: 'कुल', tax: 'कर', discount: 'छूट', shipping: 'शिपिंग' },
    fr: { subtotal: 'Sous-total', total: 'Total', tax: 'Taxe', discount: 'Remise', shipping: 'Livraison' },
    de: { subtotal: 'Zwischensumme', total: 'Gesamt', tax: 'Steuer', discount: 'Rabatt', shipping: 'Versand' },
    es: { subtotal: 'Subtotal', total: 'Total', tax: 'Impuesto', discount: 'Descuento', shipping: 'Envío' },
    ja: { subtotal: '小計', total: '合計', tax: '税', discount: '割引', shipping: '配送' },
    zh: { subtotal: '小计', total: '总计', tax: '税', discount: '折扣', shipping: '运费' },
    ur: { subtotal: 'ذیلی کل', total: 'کل', tax: 'ٹیکس', discount: 'رعایت', shipping: 'ترسیل' },
    fa: { subtotal: 'جمع جزء', total: 'جمع کل', tax: 'مالیات', discount: 'تخفیف', shipping: 'حمل' },
    tr: { subtotal: 'Ara Toplam', total: 'Toplam', tax: 'Vergi', discount: 'İndirim', shipping: 'Kargo' },
    ru: { subtotal: 'Промежог', total: 'Итого', tax: 'Налог', discount: 'Скидка', shipping: 'Доставка' },
    pt: { subtotal: 'Subtotal', total: 'Total', tax: 'Imposto', discount: 'Desconto', shipping: 'Frete' },
    bn: { subtotal: 'উপমোট', total: 'মোট', tax: 'কর', discount: 'ছাড়', shipping: 'শিপিং' },
    ta: { subtotal: 'உப மொத்தம்', total: 'மொத்தம்', tax: 'வரி', discount: 'தள்ளுபடி', shipping: 'கப்பல்' },
  };
  const tl = totalLabels[translateTargetLang];

  // ── Layer meta helper ──────────────────────────────────────────────────────
  const getLayerMeta = (el: CanvasElement) => {
    switch (el.type) {
      case 'text':
        return {
          Icon: Type,
          title: 'Text Box Label',
          detail: el.textContent
            ? el.textContent.length > 25
              ? el.textContent.slice(0, 25) + '…'
              : el.textContent
            : 'No text',
        };
      case 'table':
        return { Icon: TableIcon, title: 'Invoicing Table', detail: `${el.rows?.length ?? 0} rows` };
      case 'qr':
        return { Icon: QrCode, title: 'QR Code', detail: el.qrValue ?? '' };
      case 'barcode':
        return { Icon: BarcodeIcon, title: 'Barcode', detail: el.barcodeValue ?? '' };
      case 'stamp':
        return { Icon: Sparkles, title: 'Stamp', detail: el.stampText ?? 'APPROVED' };
      case 'shape':
        return {
          Icon: Layers,
          title: `${el.shapeType === 'circle' ? 'Circle' : 'Rectangle'} Shape`,
          detail: `Border: ${el.borderWidth ?? 0}px`,
        };
      case 'image':
        return { Icon: ImageIcon, title: 'Image', detail: 'Custom graphic' };
      case 'signature':
        return { Icon: Scissors, title: 'Signature', detail: el.signatureName ?? '' };
      case 'serial':
        return { Icon: Hash, title: 'Serial Counter', detail: formatSerialNumber(el.serialSettings) };
      case 'drawing':
        return { Icon: Sliders, title: 'Freehand Drawing', detail: '' };
      default:
        return { Icon: Type, title: el.type, detail: '' };
    }
  };

  // ── Element renderer (clean, no editor chrome) ────────────────────────────
  const renderElement = (el: CanvasElement) => {
    const totals = el.type === 'table' ? computeTableTotals(el) : null;

    return (
      <div
        key={el.id}
        onClick={(e) => { e.stopPropagation(); setPrintSelectedElementId(el.id); }}
        style={{
          position: 'absolute',
          left: el.x,
          top: el.y,
          width: el.width,
          height: el.height,
          zIndex: el.zIndex,
          opacity: (el.opacity ?? 100) / 100,
          overflow: el.type === 'stamp' ? 'visible' : undefined,
        }}
        className={`select-none transition-shadow ${
          printSelectedElementId === el.id ? 'ring-2 ring-dashed ring-blue-500 bg-blue-50/5' : ''
        } ${el.type === 'stamp' ? 'overflow-visible' : ''}`}
      >
        {/* SHAPE */}
        {el.type === 'shape' && (() => {
          const bg = el.backgroundColor || 'transparent';
          const bColor = el.borderColor || '#000';
          const bW = el.borderWidth || 0;
          const shp = el.shapeType || 'rectangle';
          if (shp === 'circle') return <div style={{ width: '100%', height: '100%', backgroundColor: bg, border: bW > 0 ? `${bW}px solid ${bColor}` : 'none', borderRadius: '9999px' }} />;
          if (shp === 'triangle') return <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none"><polygon points="50,5 95,95 5,95" fill={bg} stroke={bColor} strokeWidth={bW * 2} strokeLinejoin="round" /></svg>;
          if (shp === 'star') return <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none"><polygon points="50,2 64,36 100,36 71,57 81,91 50,70 19,91 29,57 0,36 36,36" fill={bg} stroke={bColor} strokeWidth={bW * 2} strokeLinejoin="round" /></svg>;
          if (shp === 'line') return <div className="w-full h-full flex items-center justify-center"><div style={{ width: '105%', height: Math.max(2, bW), backgroundColor: bColor || bg || '#000' }} /></div>;
          if (shp === 'arrow') return <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none"><path d="M 5,40 L 70,40 L 70,15 L 95,50 L 70,85 L 70,60 L 5,60 Z" fill={bg} stroke={bColor} strokeWidth={bW} strokeLinejoin="round" /></svg>;
          return <div style={{ width: '100%', height: '100%', backgroundColor: bg, border: bW > 0 ? `${bW}px solid ${bColor}` : 'none', borderRadius: el.borderRadius ? `${el.borderRadius}px` : '0' }} />;
        })()}

        {/* DRAWING */}
        {el.type === 'drawing' && el.drawingPoints && (
          <svg viewBox={`0 0 ${el.width} ${el.height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
            <path d={`M ${el.drawingPoints.map(p => `${p.x} ${p.y}`).join(' L ')}`} stroke={el.color || '#4f46e5'} strokeWidth={el.borderWidth || 3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}

        {/* IMAGE */}
        {el.type === 'image' && el.textContent && (
          <img src={el.textContent} alt="asset" className="w-full h-full object-contain pointer-events-none" />
        )}

        {/* TEXT */}
        {el.type === 'text' && (
          <div style={{
            fontSize: el.fontSize ? `${el.fontSize}px` : '14px',
            fontFamily: el.fontFamily || 'Inter',
            fontWeight: el.fontWeight || 'normal',
            fontStyle: el.fontStyle || 'normal',
            color: el.color || '#000',
            textAlign: el.textAlign || 'left',
            lineHeight: el.lineHeight || 1.3,
            whiteSpace: 'pre-wrap',
            transform: `rotate(${el.rotation || 0}deg)`,
            transformOrigin: 'center',
          }} className="w-full p-1 break-words">
            {el.textContent || ''}
          </div>
        )}

        {/* STAMP */}
        {el.type === 'stamp' && (() => {
          const bClr = el.color || '#dc2626';
          return (
            <div
              style={{ transform: `rotate(${el.rotation || 0}deg)`, color: bClr, borderColor: bClr, fontSize: el.fontSize ? `${el.fontSize}px` : '18px' }}
              className={`w-full h-full text-center font-black tracking-widest uppercase flex flex-col justify-center items-center bg-transparent relative leading-none ${['circle', 'oval'].includes(el.stampShape || '') ? '' : 'border-4 border-double rounded'}`}
            >
              {el.stampShape === 'circle' && <div className="absolute inset-0 border-4 border-double rounded-full" style={{ borderColor: bClr }}><div className="absolute inset-1 border border-dashed rounded-full" style={{ borderColor: `${bClr}55` }} /></div>}
              {el.stampShape === 'oval' && <div className="absolute inset-0 border-4 border-double rounded-[50%]" style={{ borderColor: bClr }}><div className="absolute inset-1 border border-dashed rounded-[50%]" style={{ borderColor: `${bClr}55` }} /></div>}
              {el.stampShape === 'pill' && <div className="absolute inset-0 border-4 border-double rounded-full" style={{ borderColor: bClr, borderRadius: '9999px' }}><div className="absolute inset-1 border border-dashed rounded-full" style={{ borderColor: `${bClr}55` }} /></div>}
              {el.stampShape === 'badge' && <div className="absolute inset-0 border-4 border-double rounded-xl" style={{ borderColor: bClr }}><div className="absolute inset-1 border border-dashed rounded-lg" style={{ borderColor: `${bClr}55` }} /></div>}
              {(!el.stampShape || el.stampShape === 'rectangle') && <div className="border w-full h-full absolute inset-0.5" style={{ borderColor: `${bClr}66` }} />}
              {el.stampDraft && <div className="absolute inset-0 opacity-10 mix-blend-color-dodge pointer-events-none" style={{ backgroundImage: 'radial-gradient(ellipse at center, transparent 0%, rgba(255,255,255,0.75) 100%), repeating-linear-gradient(45deg, transparent, transparent 1px, #fff 1px, #fff 2px)' }} />}
              <span className="relative z-10 select-none block px-1">{el.stampText || 'APPROVED'}</span>
            </div>
          );
        })()}

        {/* SIGNATURE */}
{el.type === 'signature' && (
  <div style={{ color: el.color || '#0f172a' }} className="w-full h-full flex flex-col justify-end items-center p-1 pb-1.5 select-none">
    <span style={{ fontFamily: el.fontFamily || 'Alex Brush', fontSize: el.fontSize ? `${el.fontSize}px` : '24px' }} className="tracking-wide text-center w-full truncate">
      {el.signatureName || 'Authorized'}
    </span>
    <div className="w-full h-px bg-slate-400/80 my-1" />
    <div className="flex w-full justify-center items-center gap-1" style={{overflow:'hidden'}}>
      <span style={{fontSize:'7px',color:'#64748b',fontWeight:'bold',textTransform:'uppercase',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',flex:'1',textAlign:'center'}}>
        {el.textContent || 'Authorized Signature'}
      </span>
      {bilingualMode && (el as any).arabicContent && (
        <span style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic",sans-serif',direction:'rtl',color:'#64748b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',flex:'1',textAlign:'center'}}>
          {(el as any).arabicContent}
        </span>
      )}
    </div>
  </div>
)}

        {/* QR */}
        {el.type === 'qr' && (
          <div className="w-full h-full bg-white p-1 flex items-center justify-center ring-1 ring-slate-150">
            {qrDataUrls[el.id] ? (
              <img src={qrDataUrls[el.id]} className="w-full h-full object-contain" alt="QR" />
            ) : (
              <div className="w-full h-full grid" style={{ gridTemplateColumns: `repeat(${generateQRMatrix(el.qrValue || '').length}, minmax(0,1fr))` }}>
                {generateQRMatrix(el.qrValue || '').map((row, rIdx) =>
                  row.map((isDark, cIdx) => <div key={`${rIdx}-${cIdx}`} className={isDark ? 'bg-slate-900' : 'bg-white'} />)
                )}
              </div>
            )}
          </div>
        )}

        {/* BARCODE */}
        {el.type === 'barcode' && (
          <div className="w-full h-full bg-white px-2 py-1 flex flex-col justify-between items-center ring-1 ring-slate-150 rounded">
            <div className="w-full flex-1 flex items-stretch">
              {generateCode39Bars(el.barcodeValue || '').stripes.map((stripe, sIdx) => (
                <div key={sIdx} style={{ flexGrow: stripe.width, backgroundColor: stripe.isBlack ? '#000' : 'transparent', height: '80%' }} />
              ))}
            </div>
            <span className="text-[9px] font-mono font-bold">{el.barcodeValue}</span>
          </div>
        )}

        {/* SERIAL */}
        {el.type === 'serial' && (
          <div
            style={{
              fontSize: el.fontSize ? `${el.fontSize}px` : '12px',
              fontFamily: el.serialStyle === 'classic_lcd' ? 'JetBrains Mono, monospace' : el.serialStyle === 'handwritten' ? 'Times New Roman, serif' : el.fontFamily || 'Inter',
              color: el.serialStyle === 'classic_lcd' ? '#22c55e' : el.color || '#ea580c',
              backgroundColor: el.serialStyle === 'classic_lcd' ? '#0f172a' : 'transparent',
            }}
            className={`p-1 px-2 border w-full h-full flex items-center font-mono font-bold tracking-widest ${
              el.serialStyle === 'classic_lcd' ? 'border-slate-800 rounded' :
              el.serialStyle === 'handwritten' ? 'border-dashed border-slate-300 italic' :
              el.serialStyle === 'badge_pill' ? 'border-amber-500 bg-amber-50/40 rounded-full' :
              'border-dashed border-teal-600/30 rounded bg-teal-50/50'
            }`}
          >
            {formatSerialNumber(el.serialSettings)}
          </div>
        )}

        {/* TABLE */}
        {el.type === 'table' && el.columns && el.rows && totals && (
          <div style={{ fontFamily: el.fontFamily || 'Inter' }} className="w-full h-full bg-slate-50 border border-slate-200 flex flex-col rounded p-1">
            <div style={{ backgroundColor: el.color || '#1e293b', color: el.color === '#ffffff' ? '#1e293b' : '#fff' }} className="flex text-[10px] font-bold p-1 rounded-t shrink-0">
              {el.columns.map(col => (
                <div key={col.id} style={{ width: `${col.width}%` }} className={`px-1 truncate ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}>
                  {col.header}
                </div>
              ))}
            </div>
            <div className="flex-1 overflow-hidden text-[10px] text-slate-800">
              {el.rows.map((row, rIdx) => (
                <div key={row.id} className={`flex p-1 ${rIdx % 2 === 1 ? 'bg-blue-50/25' : 'bg-white'}`}>
                  {el.columns!.map(col => {
                    let display = String(row.values[col.key] ?? '');
                    if (isSerialNoColumn(col)) display = String(rIdx + 1);
                    else if (col.key === 'total') display = `${currencySymbol}${((parseFloat(String(row.values.qty)) || 0) * (parseFloat(String(row.values.rate)) || 0)).toFixed(2)}`;
                    else if (col.key === 'rate') display = `${currencySymbol}${(parseFloat(display) || 0).toFixed(2)}`;
                    return (
                      <div key={col.id} style={{ width: `${col.width}%` }} className={`px-1 truncate ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}>
                        {display}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className={`mt-auto shrink-0 bg-slate-100 p-1.5 rounded border border-slate-200 text-[9px] space-y-0.5 ${el.totalsLayout === 'full' ? 'w-full' : 'self-end w-[45%] mr-1 mb-1'}`}>
  <div className="flex justify-between items-start">
    <div><span>Subtotal:</span>{bilingualMode && tl && <div style={{fontSize:'7px',color:'#94a3b8',marginTop:'1px'}}>{tl.subtotal}</div>}</div>
    <span className="font-mono">{currencySymbol}{totals.subtotal.toFixed(2)}</span>
  </div>
  {((el as any).taxes || []).filter((t: any) => t.rate > 0).map((t: any, i: number) => (
    <div key={i} className="flex justify-between items-start text-slate-600">
      <div><span>{t.label} +{t.rate}%:</span>{bilingualMode && tl && <div style={{fontSize:'7px',color:'#94a3b8',marginTop:'1px'}}>{t.arabicLabel || tl.tax} ({t.rate}%)</div>}</div>
      <span className="font-mono">{currencySymbol}{(totals.subtotal * t.rate / 100).toFixed(2)}</span>
    </div>
  ))}
  {((el as any).discounts || []).filter((d: any) => d.rate > 0).map((d: any, i: number) => (
    <div key={i} className="flex justify-between items-start text-red-500">
      <div><span>{d.label} -{d.rate}%:</span>{bilingualMode && tl && <div style={{fontSize:'7px',marginTop:'1px'}}>{d.arabicLabel || tl.discount} (-{d.rate}%)</div>}</div>
      <span className="font-mono">-{currencySymbol}{(totals.subtotal * d.rate / 100).toFixed(2)}</span>
    </div>
  ))}
  {el.shippingCost ? <div className="flex justify-between items-start text-slate-600"><span>Shipping:</span><span className="font-mono">{currencySymbol}{totals.shipping.toFixed(2)}</span></div> : null}
  <div className="flex justify-between items-start font-bold text-blue-700 border-t border-slate-200 pt-0.5">
    <div><span>TOTAL:</span>{bilingualMode && tl && <div style={{fontSize:'7px',marginTop:'1px'}}>{tl.total}</div>}</div>
    <span className="font-mono">{currencySymbol}{totals.total.toFixed(2)}</span>
  </div>
</div>
          </div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 md:p-6 select-none overflow-hidden">
      <div className="bg-slate-900 border border-slate-800 text-white rounded-2xl w-full max-w-7xl h-[92vh] flex flex-col shadow-2xl overflow-hidden">

        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shrink-0">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-600 rounded-lg"><Eye className="w-4 h-4" /></div>
              <h3 className="text-sm font-extrabold tracking-tight">Print & Paper Inspector</h3>
              <span className="text-[10px] bg-emerald-950 text-emerald-400 px-2 py-0.5 rounded font-bold border border-emerald-900/30">Live Vector Layout</span>
            </div>
            <p className="text-xs text-slate-400 font-semibold">
              A perfect representation of your invoice as it would print on {pageConfig.name} ({pageConfig.unitName}).
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            
            <div className="h-6 w-px bg-slate-800 hidden sm:block" />
            <button
              onClick={() => { setIsPrintPreviewOpen(false); setPrintSelectedElementId(null); }}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <span className="text-xl leading-none">&times;</span>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0 bg-slate-950">

          {/* Left layers panel */}
          <div className="w-full md:w-80 border-b md:border-b-0 md:border-r border-slate-800 flex flex-col shrink-0 min-h-0 overflow-hidden bg-slate-950">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between text-slate-400">
              <span className="text-[10px] font-black tracking-widest uppercase">Layers ({elements.length})</span>
              <span className="text-[9px] bg-slate-800 text-slate-350 px-2 py-0.5 rounded font-mono font-bold">Z Ordered</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {[...elements].sort((a, b) => b.zIndex - a.zIndex).map((el) => {
                const isSelectedInPrint = printSelectedElementId === el.id;
                const isHidden = hiddenElementIds.includes(el.id);
                const { Icon, title, detail } = getLayerMeta(el);

                return (
                  <div
                    key={el.id}
                    onClick={() => setPrintSelectedElementId(el.id)}
                    className={`p-3 rounded-lg border transition-all text-xs flex flex-col gap-1.5 cursor-pointer relative ${
                      isSelectedInPrint ? 'bg-purple-700/40 border-blue-500 ring-1 ring-blue-500/50 text-white' :
                      isHidden ? 'bg-slate-900/30 border-slate-800 opacity-50 text-slate-500' :
                      'bg-slate-900 border-slate-800 hover:bg-slate-800/60 text-slate-300'
                    }`}
                  >
                    {isSelectedInPrint && <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />}
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`p-1 rounded ${isSelectedInPrint ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold truncate text-[11px]">{title}</div>
                          {detail && <div className="text-[9px] text-slate-400 font-mono truncate max-w-[130px] mt-0.5">{detail}</div>}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setHiddenElementIds(prev =>
                            isHidden ? prev.filter(id => id !== el.id) : [...prev, el.id]
                          );
                        }}
                        className="p-1 hover:bg-slate-800 rounded transition-colors text-slate-400"
                        title={isHidden ? 'Show' : 'Hide'}
                      >
                        {isHidden ? <EyeOff className="w-3.5 h-3.5 text-rose-500" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>

                    {/* Z-order controls */}
                    <div className="flex items-center justify-between pt-1.5 border-t border-slate-800 text-[8.5px] font-bold text-slate-400">
                      <div className="flex items-center gap-1">
                        {(['front', '▲', '▼', 'back'] as const).map((action) => (
                          <button
                            key={action}
                            onClick={(e) => { e.stopPropagation(); handleZIndex(el.id, action === '▲' ? 'up' : action === '▼' ? 'down' : action); }}
                            className="px-1.5 py-0.5 bg-slate-800 hover:bg-blue-600 hover:text-white rounded transition-colors capitalize"
                          >
                            {action}
                          </button>
                        ))}
                      </div>
                      <span className="font-mono bg-slate-800 px-1 py-0.5 rounded border border-slate-700">Z:{el.zIndex}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Canvas pane */}
          <div
            className="flex-1 bg-slate-900 p-4 md:p-8 overflow-auto flex flex-col items-center justify-start"
            onClick={() => setPrintSelectedElementId(null)}
          >
            <div className="mb-4 w-full max-w-lg bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 flex items-start gap-2.5 shadow-md">
              <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              <div className="space-y-0.5 text-xs">
                <p className="font-bold text-slate-300">Pristine Paper Layout</p>
                <p className="text-slate-400 font-semibold leading-relaxed">
                  Click any element to highlight its layer. Toggle 👁️ to hide from print output.
                </p>
              </div>
            </div>

            <div className="w-full flex justify-center py-2 shrink-0">
              <div
                id="p_preview_sheet_wrapper"
                style={{ width: pageConfig.width, height: computedHeight, position: 'relative' }}
                className="bg-white border border-slate-300 shadow-2xl overflow-visible origin-top scale-[0.65]"
              >
                {elements
                  .filter(el => !hiddenElementIds.includes(el.id))
                  .sort((a, b) => a.zIndex - b.zIndex)
                  .map(renderElement)}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrintPreviewModal;